import { ThemeIcon } from '@mantine/core';
import {
  IconBell,
  IconCalendarEvent,
  IconConfetti,
  IconFileText,
  IconHammer,
  IconHome,
  IconNote,
  IconTool,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { DashboardEventDto } from 'src/Services/API/generated';
import { trackHomeCardLinkClicked } from 'src/Services/Mixpanel/events';
import { FALLBACK_HOUSEHOLD_COLOR } from 'src/Utils/eventVisuals';

import { CardItem, TwoColumnCard } from '../TwoColumnCard';

interface Props {
  otherEvents: DashboardEventDto[];
  stays: DashboardEventDto[];
}

// Same Tabler icons as the Calendar v2, keyed on the type strings the API sends.
const ICON_BY_TYPE: Record<string, typeof IconCalendarEvent> = {
  ENTRETIEN: IconTool,
  WORK: IconHammer,
  END_OF_CONTRACT: IconFileText,
  ACTIVITIES: IconConfetti,
  OTHER: IconNote,
  PRACTICAL_INFORMATION: IconBell,
};

const formatShortDate = (iso: string, language: string) =>
  dayjs(iso).locale(language).format('ddd D MMM');

const formatDateRange = (start: string, end: string, language: string) => {
  const s = dayjs(start).locale(language);
  const e = dayjs(end).locale(language);
  if (s.isSame(e, 'day')) return s.format('ddd D MMM');
  if (s.isSame(e, 'month')) return `${s.format('D')}–${e.format('D MMM')}`;
  return `${s.format('D MMM')} – ${e.format('D MMM')}`;
};

const EventsCard = ({ otherEvents, stays }: Props) => {
  const { t, i18n } = useTranslation();
  const assetId = useCurrentAssetId();

  const buildEventItem = (event: DashboardEventDto) => {
    const Icon = ICON_BY_TYPE[event.type] ?? IconCalendarEvent;
    // Same labels as the planning: `dashboard.cards.events.types` was never translated.
    const typeLabel = t(`createEventForm.eventTypes.${event.type}`, { defaultValue: event.type });
    return (
      <CardItem
        key={event.id}
        leading={
          <ThemeIcon variant="light" color="gray" size={28} radius="md">
            <Icon size={16} />
          </ThemeIcon>
        }
        title={event.name}
        subtitle={typeLabel}
        trailing={formatShortDate(event.startTime, i18n.language)}
      />
    );
  };

  const buildStayItem = (event: DashboardEventDto) => {
    const color = event.householdColor ?? FALLBACK_HOUSEHOLD_COLOR;
    return (
      <CardItem
        key={event.id}
        leading={
          <span
            aria-hidden
            style={{
              display: 'inline-block',
              width: 10,
              height: 32,
              borderRadius: 5,
              background: color,
            }}
          />
        }
        title={
          <>
            <span
              aria-hidden
              style={{
                display: 'inline-block',
                width: 9,
                height: 9,
                borderRadius: 2,
                marginRight: 6,
                verticalAlign: 'middle',
                background: color,
              }}
            />
            {event.name}
          </>
        }
        trailing={formatDateRange(event.startTime, event.endTime, i18n.language)}
      />
    );
  };

  return (
    <TwoColumnCard
      title={t('dashboard.cards.house.title', 'À la maison')}
      linkLabel={t('dashboard.cards.house.link', 'Voir le calendrier')}
      linkTo={`/asset/${assetId}/planning`}
      onLinkClick={() => trackHomeCardLinkClicked({ card: 'events' })}
      accent="bluegray"
      icon={<IconHome size={20} stroke={1.75} />}
      left={{
        title: t('dashboard.cards.house.events', 'Prochains événements'),
        count: otherEvents.length,
        items: otherEvents.map(buildEventItem),
        empty: t('dashboard.cards.house.eventsEmpty', 'Rien de prévu ce mois-ci'),
      }}
      right={{
        title: t('dashboard.cards.house.stays', 'Prochains séjours'),
        count: stays.length,
        items: stays.map(buildStayItem),
        empty: t('dashboard.cards.house.staysEmpty', 'Aucun séjour prévu'),
      }}
    />
  );
};

export default EventsCard;
